import { useRef, useEffect, useState } from 'react';

interface HowWeWorkProps {
  onCTAClick: () => void;
}

const steps = [
  {
    number: '01',
    icon: 'ri-stethoscope-line',
    phase: 'DIAGNOSE',
    color: '#00D4FF',
    gradientFrom: '#00D4FF',
    gradientTo: '#0099CC',
    title: '深度產業診斷',
    subtitle: '定位專屬獲客戰略',
    description:
      '揪出高單價實體門市行政、行銷流程漏洞，精準定位獲客瓶頸，為後續系統建置與代操策略奠定數據基礎。',
    highlights: ['高單價門市流程全面漏洞診斷', '行銷瓶頸精準定位', '獲客戰略藍圖規劃'],
    dataLabel: '診斷報告',
  },
  {
    number: '02',
    icon: 'ri-shield-keyhole-line',
    phase: 'BUILD',
    color: '#D4AF37',
    gradientFrom: '#D4AF37',
    gradientTo: '#F0C040',
    title: '企業模組導入與串接',
    subtitle: 'API 自動化閉環建置',
    description:
      '為您的商業邏輯進行深度參數設定與 API 自動化串接。實作內部權限與防呆業績回報，讓管理不再靠口頭約定。',
    highlights: ['API 自動化串接建置', '員工權限與防呆模組', '免登入品牌展示空間'],
    dataLabel: '系統上線',
  },
  {
    number: '03',
    icon: 'ri-radar-line',
    phase: 'SCALE',
    color: '#00D4FF',
    gradientFrom: '#00D4FF',
    gradientTo: '#0099CC',
    title: '數據獲客代操',
    subtitle: '全鏈路廣告轉換率優化',
    description:
      '啟用數據雷達 (GTM/GA4)，全鏈路廣告精準追蹤與轉換率優化，讓每一筆預算追蹤到最終成交。',
    highlights: ['GTM/GA4 追蹤代碼埋設', '全鏈路轉換率監控', '進階再行銷廣告放量'],
    dataLabel: '成交追蹤',
  },
];

function AnimatedConnector({ color }: { color: string }) {
  return (
    <div className="hidden lg:flex items-center justify-center relative w-16 flex-shrink-0">
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="w-1.5 h-1.5 rounded-full"
            style={{
              backgroundColor: color,
              opacity: 0.3 + i * 0.14,
              boxShadow: `0 0 6px ${color}`,
            }}
          />
        ))}
      </div>
      <div
        className="w-full h-[2px] relative overflow-hidden"
        style={{ background: `linear-gradient(90deg, transparent, ${color}40, ${color}, ${color}40, transparent)` }}
      >
        <div
          className="absolute inset-y-0 w-1/3 h-full"
          style={{
            background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
            animation: 'slideRight 2s linear infinite',
          }}
        />
      </div>
      <div
        className="absolute right-0 w-0 h-0"
        style={{
          borderLeft: `8px solid ${color}`,
          borderTop: '5px solid transparent',
          borderBottom: '5px solid transparent',
          filter: `drop-shadow(0 0 4px ${color})`,
        }}
      />
    </div>
  );
}

function StepCard({ step }: { step: typeof steps[0] }) {
  const isGold = step.color === '#D4AF37';
  return (
    <div
      className="flex-1 relative rounded-2xl overflow-hidden group cursor-default"
      style={{
        background: isGold
          ? 'linear-gradient(145deg, #1A1200, #0D1A2A)'
          : 'linear-gradient(145deg, #0D2035, #0A192F)',
        border: `1.5px solid ${step.color}30`,
        boxShadow: `0 0 30px ${step.color}08`,
        transition: 'all 0.4s ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 40px ${step.color}20, 0 0 80px ${step.color}08`;
        (e.currentTarget as HTMLDivElement).style.borderColor = `${step.color}60`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 30px ${step.color}08`;
        (e.currentTarget as HTMLDivElement).style.borderColor = `${step.color}30`;
      }}
    >
      {/* 頂部發光線條 */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, transparent, ${step.color}, transparent)` }}
      />
      {/* 背景大數字水印 */}
      <div
        className="absolute top-3 right-4 text-[80px] font-black select-none leading-none"
        style={{ color: `${step.color}06` }}
      >
        {step.number}
      </div>

      <div className="p-7 relative z-10">
        <div className="flex items-center justify-between mb-5">
          <span
            className="text-[10px] font-black tracking-[0.3em] uppercase px-3 py-1 rounded-full"
            style={{ color: step.color, background: `${step.color}15`, border: `1px solid ${step.color}30` }}
          >
            {step.number} — {step.phase}
          </span>
          <span
            className="text-[10px] font-semibold tracking-wide uppercase flex items-center gap-1"
            style={{ color: `${step.color}90` }}
          >
            <i className="ri-radio-button-line text-xs" style={{ color: step.color }}></i>
            {step.dataLabel}
          </span>
        </div>

        <div
          className="w-12 h-12 flex items-center justify-center rounded-xl mb-4"
          style={{
            background: `linear-gradient(135deg, ${step.gradientFrom}, ${step.gradientTo})`,
            boxShadow: `0 4px 20px ${step.color}30`,
          }}
        >
          <i className={`${step.icon} text-2xl text-white`}></i>
        </div>

        <h3 className="text-xl font-bold text-white mb-1">{step.title}</h3>
        <p className="text-xs font-semibold mb-4" style={{ color: step.color }}>{step.subtitle}</p>
        <p className="text-gray-400 text-sm leading-relaxed mb-5">{step.description}</p>

        <div
          className="rounded-xl p-4 space-y-2"
          style={{ background: `${step.color}08`, border: `1px solid ${step.color}15` }}
        >
          {step.highlights.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <div
                className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: `${step.color}20` }}
              >
                <i className="ri-check-line text-[10px]" style={{ color: step.color }}></i>
              </div>
              <span className="text-gray-300 text-xs font-medium">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HowWeWork({ onCTAClick }: HowWeWorkProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-28 bg-[#0A192F] relative overflow-hidden">
      <style>{`
        @keyframes slideRight {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes radarPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* 背景網格 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'linear-gradient(#00D4FF 1px, transparent 1px), linear-gradient(90deg, #00D4FF 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#00D4FF]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div
          className="absolute left-0 right-0 h-[1px] opacity-20"
          style={{
            background: 'linear-gradient(90deg, transparent, #00D4FF, transparent)',
            top: '30%',
            animation: 'radarPulse 4s ease-in-out infinite',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        {/* 標題 */}
        <div
          className="text-center mb-10 sm:mb-16"
          style={isVisible ? { animation: 'fadeInUp 0.6s ease forwards' } : { opacity: 0 }}
        >
          <div className="inline-flex items-center gap-2 bg-[#00D4FF]/10 border border-[#00D4FF]/30 text-[#00D4FF] px-4 py-2 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-widest mb-5">
            <i className="ri-route-line"></i>
            <span>全鏈數據雷達 · 全鏈路閉環流程</span>
          </div>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            三步驟建立您的
            <span className="bg-gradient-to-r from-[#00D4FF] to-[#D4AF37] bg-clip-text text-transparent"> 數位印鈔雷達</span>
          </h2>
          <p className="text-gray-400 text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed">
            從產業診斷到模組導入，再到數據放量，每一個節點都是精準可量化的數位資產。
          </p>
        </div>

        {/* 流程圖 */}
        <div
          className="flex flex-col lg:flex-row items-stretch gap-0 lg:gap-0 mb-14"
          style={isVisible ? { animation: 'fadeInUp 0.8s ease 0.2s forwards', opacity: 0 } : { opacity: 0 }}
        >
          {steps.map((step, index) => (
            <div key={index} className="contents">
              <StepCard step={step} />
              {index < steps.length - 1 && (
                <>
                  <AnimatedConnector color="#00D4FF" />
                  <div className="lg:hidden flex flex-col items-center py-3">
                    <div className="w-[2px] h-8 bg-gradient-to-b from-[#00D4FF]/60 to-[#D4AF37]/60" />
                    <div className="w-0 h-0 border-t-[8px] border-t-[#D4AF37] border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent" />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* 中段 CTA 按鈕 */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10"
          style={isVisible ? { animation: 'fadeInUp 0.8s ease 0.35s forwards', opacity: 0 } : { opacity: 0 }}
        >
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <i className="ri-checkbox-circle-line text-[#00D4FF]"></i>
            <span>看完三步驟，準備好了嗎？</span>
          </div>
          <button
            onClick={onCTAClick}
            className="group relative inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer overflow-hidden transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #00D4FF20, #D4AF3720)',
              border: '1.5px solid #00D4FF60',
              color: '#fff',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #00D4FF30, #D4AF3730)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = '#00D4FF';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 24px #00D4FF30';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'linear-gradient(135deg, #00D4FF20, #D4AF3720)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = '#00D4FF60';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
            }}
          >
            <i className="ri-rocket-2-line text-[#00D4FF]"></i>
            <span>立即啟動我的獲客引擎</span>
            <i className="ri-arrow-right-line text-[#D4AF37] group-hover:translate-x-1 transition-transform"></i>
          </button>
        </div>

        {/* 閉環成果橫幅 */}
        <div
          className="relative rounded-2xl sm:rounded-3xl overflow-hidden"
          style={isVisible ? { animation: 'fadeInUp 0.8s ease 0.4s forwards', opacity: 0 } : { opacity: 0 }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F2744] to-[#1A1200]" />
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent" />

          <div className="relative z-10 p-6 sm:p-10 flex flex-col lg:flex-row items-center justify-between gap-6 sm:gap-8">
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#F0C040] rounded-2xl flex-shrink-0">
                <i className="ri-loop-right-line text-3xl text-[#060F1E]"></i>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 justify-center sm:justify-start">
                  <span className="text-[10px] text-[#D4AF37] font-black tracking-[0.2em] uppercase">CLOSED LOOP</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" style={{ animation: 'radarPulse 2s ease-in-out infinite' }} />
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-white mb-1">全程顧問陪跑，數據閉環不中斷</h4>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed max-w-lg">
                  從第一次診斷到廣告穩定放量，我們的顧問全程參與。每月完整數據戰略報告，讓每一分廣告預算的去向與回報一目了然。
                </p>
              </div>
            </div>

            <div className="flex items-center gap-6 flex-shrink-0">
              {[
                { label: '診斷', icon: 'ri-search-eye-line', color: '#00D4FF' },
                { label: '導入', icon: 'ri-building-4-line', color: '#D4AF37' },
                { label: '放量', icon: 'ri-rocket-line', color: '#00D4FF' },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `${item.color}20`, border: `1.5px solid ${item.color}50` }}
                  >
                    <i className={`${item.icon} text-lg`} style={{ color: item.color }}></i>
                  </div>
                  <span className="text-[11px] font-semibold text-gray-400">{item.label}</span>
                </div>
              ))}
            </div>

            <button
              onClick={onCTAClick}
              className="w-full sm:w-auto bg-gradient-to-r from-[#D4AF37] to-[#F0C040] text-[#060F1E] px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-bold hover:shadow-lg hover:shadow-[#D4AF37]/40 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 flex-shrink-0"
            >
              <i className="ri-calendar-check-line"></i>
              立即預約免費診斷
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
